import { printDebug } from '@/lib/printDebug'
import { isBhooklyNativeServerApp, nativePrintJobs } from '@/lib/bhooklyNative'

/**
 * Bhookly Android print — native WebView bridge (preferred) or Chrome Intent.
 *
 * Multi-printer: one call carries `{ v:1, jobs:[{ station, target, text }] }`.
 *
 * IMPORTANT: only call from Android (PWA / Server APK). On a desktop browser,
 * intent:// is a no-op (or navigates the tab) and must not replace window.print().
 */

const ANDROID_PACKAGE = 'demo.bhookly.com'

/** True when KOT print should go through the Android print agent (not HTML). */
export function isAndroidPrintClient(): boolean {
  if (typeof window === 'undefined') return false
  if (isBhooklyNativeServerApp()) return true
  try {
    return /Android/i.test(navigator.userAgent || '')
  } catch {
    return false
  }
}

export interface BridgePrintJob {
  station?: string
  target?: string
  text: string
}

export function normalizeAgentTarget(raw: string | undefined | null): string {
  const t = (raw ?? '').trim()
  if (!t || /\s/.test(t)) return ''
  if (t.includes(':')) return t
  return `${t}:9100`
}

function utf8ToBase64(s: string): string {
  const bytes = new TextEncoder().encode(s)
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

function buildIntentUrl(base64: string): string {
  return (
    `intent://print#Intent;` +
    `scheme=bhooklyprint;` +
    `package=${ANDROID_PACKAGE};` +
    `S.escpos=${encodeURIComponent(base64)};` +
    `end`
  )
}

function launchBhooklyPrintIntent(base64: string): boolean {
  if (typeof window === 'undefined') return false
  const uri = buildIntentUrl(base64)
  printDebug.log('bridge', 'launching Android intent', { uriLength: uri.length })

  try {
    const iframe = document.createElement('iframe')
    iframe.setAttribute('style', 'display:none;width:0;height:0;border:0;position:absolute')
    iframe.setAttribute('aria-hidden', 'true')
    iframe.src = uri
    document.body.appendChild(iframe)
    window.setTimeout(() => {
      try {
        document.body.removeChild(iframe)
      } catch {
        /* already gone */
      }
    }, 2500)
  } catch (err) {
    printDebug.warn('bridge', 'iframe intent launch failed', err)
  }

  try {
    window.location.href = uri
    return true
  } catch (err) {
    printDebug.error('bridge', 'intent href assignment threw', err)
    return false
  }
}

/**
 * Print one or more KOT jobs via native bridge (Server APK) or Chrome Intent (PWA).
 */
export function bridgePrintJobs(jobs: BridgePrintJob[]): boolean {
  const clean = jobs
    .map((j) => ({
      station: (j.station ?? '').trim(),
      target: normalizeAgentTarget(j.target),
      text: (j.text ?? '').toString(),
    }))
    .filter((j) => j.text.trim().length > 0)

  if (clean.length === 0) {
    printDebug.log('bridge', 'no job text — nothing to print')
    return false
  }

  const doc = { v: 1 as const, jobs: clean }
  const json = JSON.stringify(doc)

  // Native Server WebView: NEVER fall through to intent:// — the WebView used
  // to block those navigations ("Blocked navigation outside …") and no paper
  // came out. Bridge-only path here.
  if (isBhooklyNativeServerApp()) {
    printDebug.log('bridge', `native printJobs (${clean.length})`, {
      stations: clean.map((j) => j.station || '(unnamed)'),
      targets: clean.map((j) => j.target || 'default'),
    })
    if (nativePrintJobs(json)) return true
    printDebug.warn('bridge', 'native printJobs unavailable — not falling back to intent in WebView')
    return false
  }

  const base64 = utf8ToBase64(json)
  printDebug.log('bridge', `firing multi-job intent (${clean.length})`, {
    base64Length: base64.length,
  })
  return launchBhooklyPrintIntent(base64)
}

export function bridgePrintSlips(slips: string[]): boolean {
  return bridgePrintJobs(slips.map((text) => ({ text })))
}

export function bridgePrintTest(): boolean {
  const now = new Date()
  const slip = [
    '   *** BHOOKLY AGENT TEST ***',
    '',
    'POS → print agent → default printer',
    now.toLocaleString(),
    '',
    'Per-station KOTs use Admin → Stations',
    'printer target (ip or ip:port).',
  ].join('\n')
  return bridgePrintJobs([{ station: 'Test', target: '', text: slip }])
}
